import { type Family, FamilyBadge } from "@/components/recipes/family-badge";
import { DataTableV2 } from "@/components/ui/data-table-v2";
import { EmptyStateV2 } from "@/components/ui/empty-state-v2";
import { ErrorState } from "@/components/ui/error-state";
import { KpiReadout } from "@/components/ui/kpi-readout";
import { PageHeaderV2 } from "@/components/ui/page-header-v2";
import { TableSkeleton } from "@/components/ui/skeleton-v2";
import { useCurrentProject } from "@/features/projects/current-project";
import { cn } from "@/lib/cn";
import { useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { BookOpen, Plus, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { type RecipeOut, useRecipes } from "./queries";

const columns: ColumnDef<RecipeOut>[] = [
  {
    id: "name",
    header: "Name",
    accessorKey: "name",
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        {row.original.has_uncommitted_changes && (
          <span className="size-1.5 rounded-full bg-[#E0A458]" />
        )}
        <span className="font-medium">{row.original.name}</span>
      </div>
    ),
  },
  {
    id: "family",
    header: "Family",
    accessorKey: "family",
    cell: ({ row }) => <FamilyBadge family={row.original.family || null} />,
  },
  {
    id: "tags",
    header: "Tags",
    accessorKey: "tags",
    cell: ({ row }) => {
      const tags = row.original.tags;
      if (tags.length === 0) return <span className="text-muted">—</span>;
      const visible = tags.slice(0, 3);
      const remaining = tags.length - visible.length;
      return (
        <div className="flex flex-wrap gap-1">
          {visible.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center rounded border border-line bg-surface px-1.5 py-0.5 font-mono text-[10px] text-muted"
            >
              {tag}
            </span>
          ))}
          {remaining > 0 && (
            <span className="inline-flex items-center text-[10px] text-muted">
              +{remaining} more
            </span>
          )}
        </div>
      );
    },
  },
  {
    id: "version",
    header: "Version",
    accessorKey: "last_committed_version",
    cell: ({ row }) => {
      const ver = row.original.last_committed_version;
      return <span className="font-mono text-muted">{ver !== null ? `v${ver}` : "—"}</span>;
    },
  },
  {
    id: "updated",
    header: "Updated",
    accessorKey: "updated_at",
    cell: ({ row }) => {
      const date = new Date(row.original.updated_at);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      let text: string;
      if (diffMins < 1) {
        text = "just now";
      } else if (diffMins < 60) {
        text = `${diffMins}m ago`;
      } else if (diffHours < 24) {
        text = `${diffHours}h ago`;
      } else if (diffDays < 7) {
        text = `${diffDays}d ago`;
      } else {
        text = date.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
          year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
        });
      }

      return <span className="text-muted">{text}</span>;
    },
  },
];

export function RecipesListPage() {
  const { projectId } = useCurrentProject();
  const [search, setSearch] = useState("");
  const [familyFilter, setFamilyFilter] = useState<Family | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const navigate = useNavigate();

  const isSearch = !!search;

  const { data, isLoading, isError, error, refetch } = useRecipes(projectId, {
    search: search || undefined,
    family: familyFilter || undefined,
    page: isSearch ? undefined : page,
    pageSize: isSearch ? undefined : pageSize,
  });

  const recipes = data?.items ?? [];

  const kpis = useMemo(() => {
    const tabular = recipes.filter((r) => r.family === "tabular").length;
    const anomaly = recipes.filter((r) => r.family === "anomaly").length;
    const forecast = recipes.filter((r) => r.family === "forecast").length;
    const unsaved = recipes.filter((r) => r.has_uncommitted_changes).length;
    return { tabular, anomaly, forecast, unsaved };
  }, [recipes]);

  if (!projectId) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeaderV2
          eyebrow="Knowledge"
          title="Recipes"
          meta={<span className="text-muted">Select a project to continue</span>}
        />
        <EmptyStateV2
          icon={<BookOpen className="size-12 text-muted" />}
          title="No project selected"
          description="Choose a project to view its recipes."
        />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeaderV2 eyebrow="Knowledge" title="Recipes" />
        <ErrorState
          message={error instanceof Error ? error.message : "Failed to load recipes"}
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  const handleNewRecipe = () => {
    navigate({ to: "/recipes/new" });
  };

  const handleFamilyToggle = (family: Family) => {
    setFamilyFilter((prev) => (prev === family ? null : family));
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeaderV2
        eyebrow="Knowledge"
        title="Recipes"
        meta={
          <span className="text-muted">
            {data?.total ?? 0} recipe{data?.total === 1 ? "" : "s"}
          </span>
        }
        action={
          <button
            onClick={handleNewRecipe}
            className="inline-flex items-center gap-2 rounded-md border border-line bg-surface px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-code-bg"
            type="button"
          >
            <Plus className="size-4" />
            New recipe
          </button>
        }
      />

      {/* Search bar + family filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" />
          <input
            type="text"
            placeholder="Search recipes…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-md border border-line bg-surface py-2 pl-10 pr-4 text-sm text-ink placeholder:text-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>

        <div className="flex items-center gap-2">
          {(["tabular", "anomaly", "forecast"] as const).map((family) => (
            <button
              key={family}
              onClick={() => handleFamilyToggle(family)}
              className={cn(
                "rounded border px-3 py-1.5 text-xs font-medium transition-colors",
                familyFilter === family
                  ? "border-accent bg-accent/10 text-accent"
                  : "border-line bg-surface text-muted hover:bg-code-bg",
              )}
              type="button"
            >
              {family}
            </button>
          ))}
        </div>
      </div>

      {/* KPI strip */}
      <div className="flex items-center gap-6">
        <KpiReadout label="Total" value={String(data?.total ?? 0)} />
        <KpiReadout label="Tabular" value={String(kpis.tabular)} />
        <KpiReadout label="Anomaly" value={String(kpis.anomaly)} />
        <KpiReadout label="Forecast" value={String(kpis.forecast)} />
        <KpiReadout label="Unsaved" value={String(kpis.unsaved)} accent={kpis.unsaved > 0} />
      </div>

      {/* Table or empty state */}
      {isLoading ? (
        <TableSkeleton rows={8} />
      ) : recipes.length === 0 ? (
        <EmptyStateV2
          icon={<BookOpen className="size-12 text-muted" />}
          title="No recipes yet"
          description="Create your first recipe to get started."
          action={
            <button
              onClick={handleNewRecipe}
              className="inline-flex items-center gap-2 rounded-md border border-line bg-surface px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-code-bg"
              type="button"
            >
              <Plus className="size-4" />
              New recipe
            </button>
          }
        />
      ) : (
        <DataTableV2
          // Backend /v1/recipes does not paginate today; slice client-side in
          // list mode so the DataTableV2 footer matches the rendered rows.
          // Search mode keeps its fixed 100-item ceiling — no slicing.
          data={
            !isSearch && recipes.length > pageSize
              ? (recipes.slice((page - 1) * pageSize, page * pageSize) as RecipeOut[])
              : (recipes as RecipeOut[])
          }
          columns={columns}
          onRowClick={(row) => navigate({ to: "/recipes/$recipeId", params: { recipeId: row.id } })}
          {...(!isSearch && {
            page,
            pageSize,
            total: data?.total ?? recipes.length,
            onPageChange: setPage,
          })}
        />
      )}
    </div>
  );
}
